import type { Connection, TripSuggestion } from './types';
import { moneyInText } from './price';

// The words a supplier (and the hotel inviting one) reads while joining, on the
// desk — kept together so the Greek can follow (DECISION-080 §1: English now,
// Greek next, the joining steps first). The rules behind them are the API's:
// whether a hotel can book a supplier comes from GET /v1/connections `standing`
// (B35), which numbers a code can reach from the sign-in's own refusal
// (DECISION-080 §8), and the line after asking for a code from the sign-in's 202
// (DECISION-080 §10). This file only says it.
//
// The phone carries the same words in app/src/joining.ts, and Eva says the
// standing in api/src/domain/standing.ts. Three copies, kept in step by hand
// until each set has one owner — raised in HANDOFF before the Greek is written.

// ── Sign-in and the join page ────────────────────────────────────────────────

// DECISION-080 §8 — said before the number is typed, not only after a refusal.
export const GREEK_MOBILES_ONLY = 'Only Greek mobile numbers get codes for now.';

// DECISION-080 §10 — the one line every number gets, known or not. The API sends
// it in the 202; this is the same words, for an older API that does not.
export const CODE_ON_ITS_WAY =
  'If this number has an account, your code is on its way. New here? A hotel you work with can invite you.';

// After Send again — still neutral: it never says a text went. It takes the
// place of the line above, never sits under it.
export const NEW_CODE_ON_ITS_WAY =
  "If this number has an account, a new code is on its way. Use the newest text — the code before it won't work any more.";

// The code field's label.
export const CODE_LABEL = 'The 6-digit code from the text';

// B31 — the API broke on our side (a throttle or an outage, which any number or
// address meets the same). Never "Check the number" or "Check the email
// address": what the person typed was fine.
export const CODE_REQUEST_FAILED = "We couldn't ask for a code just now — try again in a minute.";
export const LINK_REQUEST_FAILED = "We couldn't ask for a sign-in link just now — try again in a minute.";

// ── The invitation (N40) ─────────────────────────────────────────────────────
// The registered "Supplier invitation" and "SMS code" Entry screens on the desk
// (screens/Join.tsx). The phone says the same in app/src/joining.ts INVITATION —
// kept in step by hand, as the rest of this file is.

// DECISION-080 §4 — the terms beside the yes. George's terms are not published
// yet (the gate: published before the first real supplier, with P14): until then
// the link opens a holding page that says only that (public/terms/suppliers.html,
// in this build's own folder), and no yes records any acceptance.
export const TERMS_LINK = { url: `${import.meta.env.BASE_URL}terms/suppliers.html`, label: 'the terms for suppliers' };

const n = (k: number, one: string, many: string) => `${k} ${k === 1 ? one : many}`;

/** A day as the supplier and the hotel live it: Athens, whatever this desk's own zone is. */
export function athensDay(iso: string): string {
  // Put together from its parts: runtimes join them differently ("Saturday, 24 October" / "Saturday 24 October").
  const p = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Europe/Athens' })
      .formatToParts(new Date(iso))
      .map((x) => [x.type, x.value]),
  );
  return `${p.weekday} ${p.day} ${p.month}`;
}

/** A number shown partly hidden, kept on one line (no-break spaces; the API's words stay as they are). */
export const oneLine = (hint: string) => hint.replace(/ /g, ' ');

export const INVITATION = {
  opening: 'Opening your invitation…',
  oneMoment: 'One moment…',
  invitedBy: (person: string | undefined, hotel: string) => (person ? `${person} at ${hotel} invited you` : `${hotel} invited you`),
  history: (hotel: string, k: number, amount: string) =>
    `${hotel} settled ${n(k, 'booking', 'bookings')} for its guests recently, typically paying a supplier ${amount} a time. They want to send that work to you.`,
  noHistory: (hotel: string) => `${hotel} sends its guests' trips and transfers to the suppliers it trusts — and it wants you among them.`,
  // "today": bank details are asked once, when first needed (principle 2) — never "never".
  plan: "It starts with your phone — that's your sign-in. New here? A few questions and your first trip, about three minutes. Already set up with another hotel? Your yes just connects you. No documents, no bank details today.",
  money: (hotel: string) => `Free for you: ${hotel} pays you your full price, and the fee is the hotel's.`,
  termsBefore: 'Saying yes accepts ',
  termsAfter: '.',
  codeTo: (hint: string) => `Your code goes by text to ${hint}.`,
  hintLabel: (hint: string) => `your number ending ${hint.replace(/[^0-9]/g, '').slice(-3)}`,
  otherNumber: 'Use another number',
  numberLabel: 'Your mobile number',
  hotelNumberCannot: (hotel: string) => `The number ${hotel} gave can't get a code. Type your own mobile number.`,
  yes: 'Yes, set me up',
  notNow: 'Not now',
  notMe: 'This is not me',
  proven: "Your phone is checked — say yes and you're in.",
  provenExpired: "Your phone check has run out — tap Yes again and we'll text you a code.",
  sending: 'Sending…',
  checking: 'Checking…',
  continue: 'Continue',
  codeTitle: 'The code we just sent you',
  changeNumber: 'Change number',
  sendAgainIn: (k: number) => `You can ask for a new code in ${n(k, 'second', 'seconds')}.`,
  sendAgain: 'Send again',
  signInInstead: "You're set up — sign in with your phone to carry on.",
  signIn: 'Sign in with your phone',
  declinedTitle: 'No problem',
  declined: (hotel: string) => `${hotel} will see you said not now. If you change your mind, this same link still works for the rest of its 14 days.`,
  changedMind: 'Yes, set me up after all',
  // N40 (DECISION-080 §2): the one reminder, said only when one is coming, and how
  // it will come — "(simulated here)" in a demo environment.
  followUpLine: (day: string, by: 'text' | 'email' = 'text', simulated = false) =>
    `We'll ${by === 'email' ? 'email' : 'text'} you one reminder on ${day}, and then not again${simulated ? ' (simulated here)' : ''}.`,
  // Nothing is sent to the hotel on a "not me": it sees it on its list.
  notMePending: (hotel: string, k: number) => `In ${n(k, 'second', 'seconds')} this link stops working, and ${hotel} will see it reached the wrong person.`,
  notMeStatus: (hotel: string) => `This link stops working in a few seconds, and ${hotel} will see it reached the wrong person. Undo to keep it.`,
  undo: 'Undo',
  burnedTitle: 'Thanks — that link no longer works',
  burned: (hotel: string) => `${hotel} can see the invitation reached the wrong person. Nothing else happens.`,
  usedTitle: "You've already said yes",
  used: (hotel: string) => `Sign in with a code to carry on with ${hotel}.`,
  alreadyIn: 'Already said yes? Sign in',
  expiredTitle: 'This link has ended',
  expired: (hotel: string) => `Links work for 14 days. Ask ${hotel} to send you a fresh one — it takes them one click.`,
  invalidTitle: "This link isn't valid any more",
  invalid: 'Ask the hotel that invited you to send a fresh one — it takes them one click.',
  failed: "We couldn't open your invitation just now — try again in a minute.",
  retry: 'Try again',
  // The calls' own words (invitation.ts), kept here with the rest.
  theHotel: 'the hotel',
  noConnection: 'No connection right now — try again in a moment.',
  connectionDropped: 'The connection dropped — type the code again in a moment.',
  didNotGoThrough: "That didn't go through — try again in a moment.",
  codeDidNotGoThrough: "That didn't go through — try the code again in a moment.",
};

// ── Where the supplier stands with each hotel (B35) ─────────────────────────

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "1 May" — the day a season opens. */
export function seasonDay(opensOn: string): string {
  return `${Number(opensOn.slice(8, 10))} ${MONTHS[Number(opensOn.slice(5, 7)) - 1] ?? ''}`.trim();
}

export function hotelName(c: Connection): string {
  return c.name ?? c.hotelName ?? 'Your hotel';
}

function names(list: string[]): string {
  if (list.length <= 1) return list[0] ?? '';
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

export type Standing = NonNullable<Connection['standing']>;

/**
 * A live hotel's standing, as the API said it — the screens never work it out
 * (hard constraint 1). An older API sends none, and then nothing is claimed.
 */
export function standingOf(c: Connection): Standing | undefined {
  return c.standing;
}

function withStanding(live: Connection[], s: Standing): Connection[] {
  return live.filter((c) => standingOf(c) === s);
}

// The card's own line: who has the next step, and that it is on their screen.
function waitingLine(hotels: string[]): string {
  return hotels.length === 1
    ? `Waiting on ${hotels[0]} to list your trip — it's on their screen. Once they do, requests can come.`
    : `Waiting on ${names(hotels)} to list your trips — it's on their screens. Once one does, requests can come.`;
}

// Out of season. A hotel that has listed a trip can already book a day in the
// coming season (a booking is checked against its own day), so this never says
// "no hotel can book you": it says when the season starts, and what they can book.
function seasonTitle(c: Connection | undefined): string {
  return c?.seasonOpensOn ? `Your season starts ${seasonDay(c.seasonOpensOn)}` : "Your season hasn't started yet";
}
function seasonBooks(hotels: Connection[]): string {
  const who = names(hotels.map(hotelName));
  return hotels[0]?.seasonOpensOn ? `${who} can book days from then on.` : `${who} can book days in your season.`;
}
function seasonLine(hotels: Connection[]): string {
  return `${seasonTitle(hotels[0])} — ${seasonBooks(hotels)}`;
}

const NOT_OPEN_LINE = "You're not open for work, so no requests can come.";

/** The Connection state chip for a live hotel, by standing. */
export function standingChip(c: Connection): { kind: 'connected' | 'pending' | 'dormant'; label: string } {
  switch (standingOf(c)) {
    case 'bookable':
      return { kind: 'connected', label: 'Can book you' };
    case 'not-listed':
      return { kind: 'pending', label: 'Trip not listed yet' };
    case 'out-of-season':
      return { kind: 'pending', label: seasonTitle(c) };
    case 'not-open':
      // Theirs, not the hotel's — beside a hotel's name, "Not open for work"
      // read as the hotel's state.
      return { kind: 'dormant', label: "You're not open for work" };
    default:
      return { kind: 'connected', label: 'Connected' };
  }
}

/** One live hotel, in a sentence. */
export function standingLine(c: Connection): string {
  const name = hotelName(c);
  switch (standingOf(c)) {
    case 'bookable':
      return `${name} can book you now.`;
    case 'not-listed':
      return waitingLine([name]);
    case 'out-of-season':
      return seasonLine([c]);
    case 'not-open':
      return NOT_OPEN_LINE;
    default:
      return `You're connected with ${name}.`;
  }
}

/** Who has the next step, for the live hotels that can't book them now — one line per kind, the hotels named. */
function nextStepLines(live: Connection[]): string[] {
  // Not open for work is theirs, and the same for every hotel: the whole story.
  if (withStanding(live, 'not-open').length) return [NOT_OPEN_LINE];
  const unlisted = withStanding(live, 'not-listed');
  const season = withStanding(live, 'out-of-season');
  // Out of season with no hotel listed yet: when the season starts, said once.
  const opens = live.find((c) => c.seasonOpensOn);
  return [
    ...(unlisted.length ? [waitingLine(unlisted.map(hotelName))] : []),
    ...(season.length ? [seasonLine(season)] : opens ? [`${seasonTitle(opens)}.`] : []),
  ];
}

/** The hero's line on the first Today — said only when it is true. */
export function canBookNow(n: number): string {
  return `${n} hotel${n > 1 ? 's' : ''} can book you now`;
}

/**
 * Today's empty state: where the supplier stands. The first Today names every
 * hotel and says whether it can book them now (canon 11 step 6, true only when
 * it is); later only the hotels that cannot.
 */
export function todayStanding(
  live: Connection[],
  asking: Connection[],
  firstToday: boolean,
): { title: string; lines: string[] } {
  if (!live.length) {
    return {
      title: 'No hotel is connected yet',
      lines: [
        asking.length
          ? `Connect with ${names(asking.map(hotelName))} above, and once they list your trip, requests can come.`
          : 'A hotel you work with can invite you — once one does, it appears here. Until then no requests can come.',
      ],
    };
  }
  const bookable = withStanding(live, 'bookable');
  const unlisted = withStanding(live, 'not-listed');
  const season = withStanding(live, 'out-of-season');

  if (bookable.length) {
    return {
      title: firstToday
        ? bookable.length === 1
          ? `${hotelName(bookable[0]!)} can book you now`
          : canBookNow(bookable.length)
        : 'Nothing needs you',
      lines: [
        ...(firstToday ? [] : ['This is the good outcome, not an empty screen. Requests and connection asks land here, most urgent first.']),
        ...(firstToday && bookable.length > 1 ? bookable.map(standingLine) : []),
        ...nextStepLines(live),
      ],
    };
  }
  if (unlisted.length && season.length) {
    // A hotel out of season beside it can already book days in the season, so
    // not "no hotel can book you yet": the hotel with the next step leads.
    return {
      title: `Waiting on ${names(unlisted.map(hotelName))} to list your trip${unlisted.length > 1 ? 's' : ''}`,
      lines: [
        unlisted.length === 1
          ? "It's on their screen. Once they do, requests can come."
          : "It's on their screens. Once one does, requests can come.",
        seasonLine(season),
      ],
    };
  }
  if (unlisted.length) return { title: 'No hotel can book you yet', lines: nextStepLines(live) };
  // Every live hotel out of season — each named, on the first Today too.
  if (season.length) return { title: seasonTitle(season[0]), lines: [seasonBooks(season)] };
  const rest = nextStepLines(live);
  if (rest.length) return { title: 'No hotel can book you yet', lines: rest };
  // An older API: connected, and nothing known either way. Claim nothing.
  return { title: 'Nothing needs you right now', lines: [] };
}

// Hotels never read: nothing is claimed about them. The list is polled, so the
// line is true.
export const HOTELS_UNREAD = {
  title: 'Nothing needs you right now',
  line: "Couldn't check your hotels just now — this updates by itself.",
};

// ── The connection (Connect on Today) ───────────────────────────────────────

export const CONNECT_TASK_META = 'A hotel wants to send you guests — accept, and once they list your trip they can book you.';
/** Beside a request's own words: what Accept does next (design review CONSIDER 7 — the phone's ACCEPT_LINE). */
export const ACCEPT_LINE = 'Accept, and once they list your trip they can book you.';

/** After a Connect: what changed, said once — the standing below says who is next. */
export function connectedWith(hotel: string): string {
  return `Connected with ${hotel}.`;
}

// ── Another hotel wants you (N42, DECISION-080 §2 and §6) ───────────────────
// The supplier's side, as the phone says it (app/src/joining.ts): the card names
// the hotel and what it will see if they accept; Accept and Not now side by
// side (the desk says *Accept* everywhere, as the phone does — design review
// CONSIDER 7); Not now and End one tap each with the design system's undo toast
// and its 30-second person-window (George, N42 fix round), after which the hotel
// can see the answer; the hotel's trip as a suggestion beside theirs, never over it.

export const ANOTHER_HOTEL = {
  wants: 'Wants to connect',
  // The chip; "{by} at {hotel} invited you too." leads the row (design review SHOULD 12).
  alsoInvited: 'Also invited you',
  invitedToo: (hotel: string, by?: string) => (by ? `${by} at ${hotel} invited you too.` : `${hotel} invited you too.`),
  sees: (hotel: string) => `If you accept, ${hotel} sees your trips and what you're paid for them — never your bookings with other hotels.`,
  accept: 'Accept',
  accepting: 'Accepting…',
  notNow: 'Not now',
  saying: 'Saying not now…',
  waitsForFinish: (hotel: string) => `Finish setting up below, then you can accept ${hotel} — or say not now.`,
  saidNotNow: (hotel: string) => `You said not now to ${hotel}.`,
  undo: 'Undo',
  undoing: 'Undoing…',
  // The toast's line while it lasts, and what is true once it has closed.
  seesWhenClosed: (hotel: string) => `${hotel} sees it when this closes.`,
  hotelCanSee: (hotel: string) => `${hotel} can see your answer now. Nothing else changes, and they can ask again later.`,
  acceptLabel: (hotel: string) => `Accept ${hotel}`,
  notNowLabel: (hotel: string) => `Not now to ${hotel}`,
  undoLabel: (hotel: string) => `Undo not now to ${hotel}`,
  // My hotels' history (George, N42 fix round): a Not now stays, with an Accept.
  chip: 'Not now',
  historyLine: (day: string) => `You said not now on ${day}. They can ask again.`,
};

/** A trip's figure as it is said in running text: "€ 40.00 a trip", the sign held to the figure (price.ts moneyInText). */
function figure(m: { minor: number; currency: string }, per: 'guest' | 'job'): string {
  return `${moneyInText(m.minor, m.currency)} ${perWords(per)}`;
}

/** The line about the hotel's trip before Accept — said only when it is true. */
export function tripPreview(hotel: string, s: TripSuggestion): string {
  if (s.match === 'same') {
    return s.listsAtAccept
      ? `Accept, and ${hotel} can book your ${s.trip.name} straight away.`
      : `${hotel} lists your ${s.trip.name} once you accept.`;
  }
  const theirs = `${s.trip.name} at ${figure(s.trip.supplierPrice, s.trip.per)}`;
  if (s.match === 'differs' && s.yours) {
    return `${hotel} lists ${theirs} — yours is ${figure(s.yours.supplierPrice, s.yours.per)}. After you accept, keep yours or add theirs as a separate trip.`;
  }
  return `${hotel} lists ${theirs}. After you accept, you can add it as a trip of yours.`;
}

export const TRIP_SUGGESTION = {
  chip: 'Their trip',
  title: (hotel: string, s: TripSuggestion) => `${hotel} lists ${s.trip.name} at ${figure(s.trip.supplierPrice, s.trip.per)}`,
  // Who will see an added trip, said BEFORE the tap (design review SHOULD 9; George).
  line: (s: TripSuggestion) =>
    s.match === 'differs' && s.yours
      ? `Yours is ${figure(s.yours.supplierPrice, s.yours.per)}: keep yours, or add theirs as a separate trip — every hotel you work with would see both.`
      : 'Add it as a trip of yours — every hotel you work with will see it — or leave it.',
  keep: (s: TripSuggestion) => (s.match === 'differs' ? 'Keep mine' : 'Leave it'),
  add: (s: TripSuggestion) => (s.match === 'differs' ? 'Add theirs' : 'Add it'),
  nameLabel: 'Name, if you add it',
  nameNeeded: 'Give the trip a name to add it.',
  saving: 'Saving…',
  added: (hotel: string, name: string, listed: boolean) =>
    listed
      ? `Added: ${name} is a trip of yours now, and on ${hotel}'s list. Any hotel you work with can see it.`
      : `Added: ${name} is a trip of yours now. ${hotel} lists it when they're ready; any hotel you work with can see it.`,
  kept: (hotel: string, name: string) => `Kept your ${name} as it is. ${hotel} can list it when they're ready.`,
  // A trip new to them, not added (design review MUST 1): never "kept your …".
  left: (hotel: string, name: string) => `Left it: ${name} isn't one of your trips. ${hotel} can list one of yours when they're ready.`,
  listed: (hotel: string, name: string) => `Your ${name} is on ${hotel}'s list.`,
};

/** What the server recorded, said as it is — never the button that was tapped (correctness MUST 1). */
export function tripAnswerSaid(hotel: string, s: TripSuggestion, r: { answer: string; listed: boolean; name?: string }): string {
  if (r.answer === 'add') return TRIP_SUGGESTION.added(hotel, r.name ?? s.suggestedName ?? s.trip.name, r.listed);
  if (r.answer === 'keep') return TRIP_SUGGESTION.kept(hotel, s.yours?.name ?? s.trip.name);
  if (r.answer === 'same') return TRIP_SUGGESTION.listed(hotel, s.yours?.name ?? s.trip.name);
  return TRIP_SUGGESTION.left(hotel, s.trip.name);
}

export const END_HOTEL = {
  // Requests already waiting too (design review SHOULD 7; George: they stay answerable).
  before: (hotel: string) => `Ending stops new requests from ${hotel}. Requests already waiting from them, and bookings you've accepted, stay as they are.`,
  end: 'End',
  ending: 'Ending…',
  ended: (hotel: string) => `You ended working with ${hotel}.`,
  undo: 'Undo',
  hotelCanSee: (hotel: string) => `${hotel} can see it now. Requests already waiting and bookings you've accepted stay as they are.`,
  stays: "Requests already waiting and bookings you've accepted stay as they are.",
  endLabel: (hotel: string) => `End working with ${hotel}`,
  undoLabel: (hotel: string) => `Undo ending with ${hotel}`,
  chip: 'Ended',
  historyLine: (day: string) => `You ended it on ${day}. Your history with them is kept.`,
};

// ── The hotel's side of a request (N42) ─────────────────────────────────────
// What reached the supplier, said as it is (DECISION-080 §2: one alert, or one
// text when alerts are off, at most twice in all); their answer in kind words
// (canon 13: the hotel is told and the history kept); and after five days with
// no answer, a call on the number the hotel typed (canon 10 H4).

// N42 fix round (George's field set): ONE state, `reached` — never whether it
// was an alert or a text, which would tell a hotel the supplier's phone settings.
export type Told = { state: 'due' | 'reached' | 'simulated' | 'unknown' | 'failed' | 'no-channel' | 'asked-twice' | 'none'; at?: string };

export const ASKED = {
  requested: 'Asked to connect',
  // The pair of N40's "Invited · not now" (design review SHOULD 11).
  declined: 'Asked · not now',
  ended: 'No longer working together',
  // The front office does not know the supplier app's tab names: "waiting for
  // them in ISO", never "on their Today" (design review SHOULD 11). `phone` is
  // given only when no call prompt is on the row (CONSIDER 2: one call, said once).
  told: (t: Told | undefined, when: (iso: string) => string, phone?: string): string => {
    const call = phone ? ` Call them on ${phone}.` : '';
    switch (t?.state) {
      case 'due':
        return t.at ? `It's waiting for them in ISO. We'll let them know on ${when(t.at)}, in their daytime.` : "It's waiting for them in ISO, and we're letting them know.";
      case 'reached':
        // What is known: our message went (correctness CONSIDER 7) — never that it reached a phone.
        return `It's waiting for them in ISO, and we let them know${t.at ? ` on ${when(t.at)}` : ''}.`;
      case 'simulated':
        return "It's waiting for them in ISO. Alerts and texts are simulated here — nothing reached their phone.";
      case 'unknown':
        return `It's waiting for them in ISO. Our message may or may not have reached them — we can't tell.${call}`;
      case 'failed':
        return `It's waiting for them in ISO, but we couldn't reach their phone.${call}`;
      case 'no-channel':
        return `It's waiting for them in ISO for when they open it — we can't reach their phone.${call}`;
      case 'asked-twice':
        return `It's waiting for them in ISO. We've told them twice already, so no message went this time.${call}`;
      default:
        return "It's waiting for them in ISO.";
    }
  },
  // The one call wording (canon 10 H4), for the request row and the invitation row alike.
  call: 'No answer in 5 days — call them on',
  declinedHint: (day: string) => `They said not now on ${day}. Nothing else changes — you can ask again from Invite supplier.`,
  endedHint: (day: string) =>
    `They ended it on ${day}. No new requests can go to them; bookings already confirmed stay as they are, and your history with them is kept. You can ask again from Invite supplier.`,
};

/** How the trip the hotel offered stands (N42), from the hotel's own rows. */
export function tripOfferLine(o: { name: string; state: 'with-them' | 'listed' | 'kept-own' | 'left' | 'not-listed' }): string {
  switch (o.state) {
    case 'with-them':
      // The hotel's own step meanwhile (design review SHOULD 10).
      return `Your ${o.name} is with them — they'll keep their own trip or add yours. You can list one of their trips in Products meanwhile.`;
    case 'kept-own':
      return `They kept their own trip instead of your ${o.name} — list one of their trips in Products and set your margin.`;
    case 'left':
      return `They didn't add your ${o.name} — list one of their trips in Products and set your margin.`;
    case 'not-listed':
      return `Your ${o.name} isn't listed yet — add their trip in Products and set your margin there.`;
    default:
      return '';
  }
}

/** The invite form's answer for a number already on ISO (N42). */
export function askedWords(
  business: string,
  a: { again?: boolean; told?: Told; tripCarried?: boolean; already?: boolean; settingUp?: boolean; phone: string },
): string {
  const trip = a.tripCarried ? ' Your trip goes with it — they can keep their own or add yours.' : '';
  // Their yes on this hotel's own link, still setting up: not a request (correctness CONSIDER 8).
  if (a.settingUp) return `${business} said yes to your invitation and is setting up. Once they finish, list their trip and you can book them.`;
  if (a.already) return `${business} already has your request — it's waiting for them in ISO.`;
  if (a.told?.state === 'asked-twice') {
    return `${business} is already on ISO — your request is waiting for them again. We've told them twice already, so no message goes this time: call them on ${a.phone}.${trip}`;
  }
  // No alert and no text can reach them (correctness CONSIDER 8): never promised.
  if (a.told?.state === 'no-channel') {
    return `${business} is already on ISO — ${a.again ? "we've asked them again" : "we've asked them to connect with you instead"}: it's waiting for them in ISO for when they open it, but we can't reach their phone — call them on ${a.phone}. No second account.${trip}`;
  }
  return `${business} is already on ISO — ${a.again ? "we've asked them again" : "we've asked them to connect with you instead"}: it's waiting for them in ISO, and we'll let them know in their daytime. No second account.${trip}`;
}

// ── The first run (canon 11 E2 step 6) ──────────────────────────────────────

/** Beside Finish: it opens them for work; a hotel can book them once it has listed a trip. */
export function finishFoot(complete: boolean, waitingHotel: string | undefined): string {
  if (!complete) return 'Finish the steps above first.';
  return waitingHotel
    ? `Everything's set — open yourself for work. ${waitingHotel} can book you once they've listed your trip.`
    : "Everything's set — open yourself for work.";
}

// ── An account someone else set up (DECISION-080 §9) ────────────────────────

export const SET_UP_FOR_YOU = {
  eyebrow: 'Set up for you',
  title: "Here's what was set up for you",
  // "We": the setter is ISO's operator, and the app already speaks as "we".
  lede: "We set this up for you. Check it, starting with your price — keep it, or change anything that isn't right. No hotel can book you until you've checked it and finished setting up.",
  trips: 'Your price',
  noTrips: 'No trip was set up for you — you add your first one next.',
  business: 'Your business',
  days: 'When you work',
  noDays: 'Not set — you say which days you work next.',
  keep: "Keep this — it's right",
  keeping: 'Keeping…',
  keepHint: 'Keeping it is your yes. You can still change any of it later.',
  change: 'Change →',
  loadFailed: "Couldn't load what was set up for you.",
  retry: 'Try again',
  keepWaits: 'You can keep it once it has loaded.',
};

// ── The hotel's invitations (B19, B31) ──────────────────────────────────────

// In a simulated environment the honest words (B19): the invitation exists, and
// nothing was sent. Since N40 part 2 the invitation goes by text (DECISION-080 §2),
// the email optional.
export const INVITE_SIMULATED = 'Invitation recorded — texts and email are simulated in this environment';

export type Channel = { state: 'sent' | 'simulated' | 'failed'; reason?: string };

/**
 * Under the "Invitation recorded" heading: what happened, channel by channel,
 * without saying the heading again (N40 design review) — each channel said as it
 * went, so a refused email is never called "simulated".
 */
export function inviteSimulatedBody(a: { phone: string; email?: string; sms?: Channel; mail?: Channel }): string {
  const t = a.sms?.state === 'simulated';
  const m = a.mail?.state === 'simulated';
  const what =
    t && m
      ? `Texts and email are simulated in this environment: nothing was sent to ${a.phone} or ${a.email}.`
      : t
        ? `Texts are simulated in this environment: nothing was texted to ${a.phone}.`
        : `Email is simulated in this environment: nothing was emailed to ${a.email}.`;
  return `${what} The link works for 14 days.`;
}

/** Beside a channel that went: the other one only reached this environment's demo inbox. */
export function textSimulatedToo(phone: string): string {
  return `Texts are simulated in this environment: nothing was texted to ${phone}.`;
}

/** Under "Invitation sent": how it went, channel by channel (B19). */
export function inviteOnItsWay(a: { business: string; phone: string; email?: string; sms?: Channel; mail?: Channel }): string {
  const ways = [
    a.sms?.state === 'sent' ? `by text to ${a.phone}` : '',
    a.mail?.state === 'sent' ? `by email to ${a.email}` : '',
  ].filter(Boolean);
  return `The invitation is on its way ${ways.join(' and ')} — the link works for 14 days. You'll see ${a.business} as Setting up here once they've said yes, and Active once they've finished.`;
}

/** A channel that was refused while another went: said plainly, with the reason (B31). */
export function channelRefused(which: 'text' | 'email', reason: string | undefined): string {
  return `The ${which} could not be sent: ${reason ?? 'it was refused'}.`;
}

/** Re-send, as it happened — and a channel refused beside the one that went is never lost. */
export function resentWords(a: { phone?: string; email?: string; sms?: Channel; mail?: Channel }): string {
  const ways = [a.sms?.state === 'sent' ? `by text to ${a.phone}` : '', a.mail?.state === 'sent' ? `by email to ${a.email}` : ''].filter(Boolean);
  const refused = [
    a.sms?.state === 'failed' ? channelRefused('text', a.sms.reason) : '',
    a.mail?.state === 'failed' ? channelRefused('email', a.mail.reason) : '',
  ].filter(Boolean);
  return ways.length ? [`Sent again ${ways.join(' and ')}.`, ...refused].join(' ') : '';
}

// The field order follows what matters: the mobile first (the text goes there and
// it becomes their sign-in), then the optional email (N40 design review).
export const INVITE_EMAIL = {
  label: 'Contact email (optional)',
  hint: "The link goes here as well as by text. Useful when the number you have can't get texts, such as a landline: they open the link and sign in with their own Greek mobile.",
};

// ── Sending again: twice in 30 days, a day apart (N40 fix round) ────────────
// DECISION-080 §2, read strictly: the first send and ONE more — a Re-send or
// ISO's one reminder after Not now, whichever comes first. The API counts it
// and says so (409); the directory says it before the button is pressed.
export const RESEND = {
  label: 'Re-send',
  sending: 'Sending…',
  wait: (when: string) => `Sent less than a day ago — the same link still works. You can send it again after ${when}.`,
  twice: (phone: string | undefined) => `Sent twice already — ${phone ? `call them on ${phone}` : 'call them'}.`,
};

/** When Re-send can go again, in Athens time — the hotel's. */
export function athensWhen(iso: string): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: 'Europe/Athens' })
      .formatToParts(new Date(iso))
      .map((p) => [p.type, p.value]),
  );
  return `${parts.weekday} ${parts.day} ${parts.month}, ${parts.hour}:${parts.minute}`;
}

// ── Withdraw, and a supplier who said yes on another number (N40 fix round) ──
export const WITHDRAW = {
  label: 'Withdraw',
  busy: 'Withdrawing…',
  confirm: (name: string) => `Withdraw the invitation to ${name}? Its link stops working for good. You can invite them again later with a fresh one.`,
  confirmYes: 'Withdraw it',
  keep: 'Keep it',
  done: 'Withdrawn — that link no longer works.',
  row: 'Invitation withdrawn',
  rowHint: 'You withdrew it. You can invite them again with a fresh link.',
  // Offered after sending to a new number, never done silently.
  offer: (name: string, hint: string | undefined) =>
    `You also have an open invitation to ${name}${hint ? ` at ${hint}` : ''}. Withdraw it, so only the new number's link works?`,
};

/** A business already open for work said yes on the link: no Finish listed the trip the invitation carried (N40 review). */
export const TRIP_NOT_LISTED =
  "They were already open for work when they said yes, so the trip you sent wasn't listed. Add it in Products and set your margin there.";

/** A connection whose yes came from a number other than the one the hotel typed (security review S-C). */
export function joinedOnOtherNumber(masked: string): string {
  return `Joined on a different number than you entered: ${masked}. Check with them by phone if that's not who you meant.`;
}

// ── The hotel's Invite form and directory (N40) ─────────────────────────────

// DECISION-077: messages carry one name, the sender's, until there is a brand.
// Since N40 part 2 the invitation goes as a text from it, the email optional.
export const SENDER = 'Supplier';

export const INVITER = {
  // Asked once, remembered on the hotel user's own record (GET /v1/account user.name).
  label: 'Your name, as they know you',
  hint: "The invitation says it's from you, so they know who is asking. Your first name is enough — you type it once, and we remember it.",
  // Never a placeholder that reads as a filled-in value (design review).
  placeholder: 'e.g. Maria',
  sentAs: (name: string) => `The invitation says it's from ${name}.`,
  change: 'Change',
};

// DECISION-080 §8 — said on the invite form, before the number is typed.
export const INVITE_GREEK_ONLY =
  "The invitation goes by text to this number, and it becomes their sign-in. Only Greek mobile numbers get texts and codes for now. If it's already on ISO, this becomes a connection request instead of a new account.";

/**
 * After sending: tell them it's coming, and who it comes from — a call from
 * someone they know is what makes the link trusted. The sender as the phone shows
 * it: SUPPLIER for a text (AWS keeps a sender ID in capitals, DECISION-077),
 * Supplier on an email.
 */
export function tellThemItsComing(business: string, how: 'text' | 'email' | 'both' = 'text'): string {
  const what =
    how === 'email'
      ? `an email is on its way from "${SENDER}"`
      : how === 'both'
        ? `a text from "${SENDER_ON_TEXTS}" and an email from "${SENDER}" are on their way`
        : `a text is on its way from "${SENDER_ON_TEXTS}"`;
  return `Let ${business} know ${what} — a quick call from you is what makes the link one they trust. On their phone the link opens the app; on a computer, the website.`;
}

/** The sender ID as a phone shows it. */
export const SENDER_ON_TEXTS = 'SUPPLIER';

// The directory's row for "Not now" (canon 11 wrote `Invited · declined to join`;
// canon's wording is Cowork's to bring into line — N40 report).
export const NOT_NOW_ROW = {
  label: 'Invited · not now',
  hint: 'They said not now. The same link still works if they change their mind.',
};

/**
 * The "not now" row's line (N40): the one reminder, said as it is — coming on a
 * day, gone on a day, only to the demo inbox, may or may not have gone, could
 * not be sent, or none (DECISION-080 §2: once, then never again). The invitation
 * goes twice at most in 30 days, so a reminder that went was its second send.
 */
export function notNowHint(
  f: { state: 'due' | 'sent' | 'simulated' | 'unknown' | 'failed' | 'none'; at?: string } | undefined,
  when: (iso: string) => string,
): string {
  if (f?.state === 'due' && f.at) {
    return `They said not now. We'll remind them once on ${when(f.at)}, and then not again — sending it again yourself takes the reminder's place. The same link still works.`;
  }
  if (f?.state === 'sent' && f.at) return `They said not now, and we reminded them once on ${when(f.at)}. The same link still works — call them if you want to ask again.`;
  if (f?.state === 'simulated') return 'They said not now. The one reminder went only to the demo inbox here. The same link still works.';
  if (f?.state === 'unknown') return "They said not now. The one reminder may or may not have reached them — we can't tell. The same link still works.";
  if (f?.state === 'failed') {
    return "They said not now. The one reminder could not be sent, so they haven't been reminded. The same link still works — you can send it again, or call them.";
  }
  return NOT_NOW_ROW.hint;
}

// What the form says when the number can get no text and there is no email —
// the API's own words (api/src/handlers/suppliers.ts INVITE_NEEDS_A_WAY).
export const INVITE_NEEDS_A_WAY =
  "This number can't get texts — only Greek mobile numbers can, for now. Give their Greek mobile, or add their email: the link goes there, and they sign in with a Greek mobile of their own.";

// ── Setup on the desk (N39) ─────────────────────────────────────────────────
// Each setup form leads to the next (canon 11 E2 steps 3–5): Business profile →
// My trips → Availability → Today's Finish. My trips stays reachable after setup
// through a link, never a rail entry (DECISION-054, DECISION-081).

/** Beside Finish, when the trip they kept is the inviting hotel's own: Finish lists it (DECISION-080 §7) — within their days and season. */
export function finishFootListed(hotel: string, trip: string): string {
  return `Everything's set — Finish opens you for work and puts your ${trip} on ${hotel}'s list, so they can book you on the days you work, inside your season.`;
}

/** The first-run trip row while the inviting hotel's trip waits to be kept or changed (the phone's words too). */
export function tripRowOffered(hotel: string, trip: string): string {
  return `${hotel}'s ${trip} is ready — keep it, or change it.`;
}

export const SETUP_NEXT = {
  // Under a setup form's Save, while setting up.
  thenNext: 'Saving takes you to the next step.',
  allDone: "That's every step — Finish is on Today.",
};

export const MY_TRIPS_LINK = {
  title: 'Your trips',
  line: 'What you sell — the trips a hotel can list from you.',
  open: 'My trips →',
};

// N41 — the desk points at the phone app, on the supplier's Today after setup.
// What it says of a request is the server's ladder (api/src/domain/alerts.ts,
// api/src/sweep.ts): an alert to a phone with alerts on once the hotel's
// 30-second undo has passed and the sweeper's next minute comes — within a
// minute or two; a text about 5 minutes on, until the request is opened or
// answered — opening it here acknowledges it and cancels the text
// (api/src/handlers/bookings-read.ts); no text 22:00–07:00 unless the job starts
// within 3 hours. The desk cannot see whether a phone of theirs has alerts on
// (the server has no read of it yet — card N22), so it says both. The link is
// for the phone's own browser — opened inside Viber or Messenger, the app's own
// browser can't get alerts.
export const PHONE_APP = {
  title: 'Get requests on your phone',
  line: "Open this link in your phone's browser — Chrome, or Safari on an iPhone — and sign in with a code. Add ISO to your home screen and turn on alerts there, and a new request shows up on your phone within a minute or two of the hotel sending it.",
  until:
    "Without alerts, a request reaches you by text about 5 minutes after it arrives — unless you've opened it here first. No text goes between 22:00 and 07:00 unless the job starts within 3 hours.",
  label: 'The phone app',
  copy: 'Copy the link',
  copied: 'Copied',
  failed: "Couldn't copy — select the link and copy it.",
};

/** "a guest" or "a trip" — what the figure is for, after a figure ("€ 58.00 a guest"). */
export function perWords(per: 'guest' | 'job'): string {
  return per === 'guest' ? 'a guest' : 'a trip';
}

/** One reading of a doubtful figure in plain words — "1200 euros", "1 euro and 20 cents" (the phone's words too). */
export function amountWords(minor: number, currency: string): string {
  const whole = Math.floor(minor / 100);
  const cents = minor % 100;
  const unit = currency === 'EUR' ? (whole === 1 ? 'euro' : 'euros') : currency;
  const cent = cents ? ` and ${cents} cent${cents === 1 ? '' : 's'}` : '';
  return whole || !cents ? `${whole} ${unit}${cent}` : `${cents} cent${cents === 1 ? '' : 's'}`;
}

// Every desk money box (My trips, the hotel's invite form and margin editor,
// Eva's rule cap) reads a figure through price.ts and asks about "1.200".
export const MONEY_FIELD = {
  which: 'Which did you mean?',
  bad: 'Type a figure — for example 58 or 58,50.',
};

export const TRIP_FORM = {
  per: 'Per person, or for the whole boat or car?',
  perGuest: 'Per person',
  perJob: 'Whole boat or car',
  perAsk: 'Say per person, or for the whole boat or car.',
  priceLabel: 'What you get paid',
  priceBad: 'Type the figure you are paid — for example 58 or 58,50.',
  // A new trip asks how long and how many, as the phone does, so the over-capacity
  // refusal (DECISION-074) covers trips made at the desk too (N39 review).
  duration: (required: boolean) => (required ? 'Duration (hours)' : 'Duration (hours, optional)'),
  guests: (required: boolean) => (required ? 'Max guests' : 'Max guests (optional)'),
  durationBad: 'A number of hours, up to 24 — for example 6 or 1,5.',
  durationNeeded: 'Say how long it takes, in hours.',
  guestsBad: 'A whole number of people — for example 12.',
  guestsNeeded: 'Say how many it takes at most.',
  // The prefill (DECISION-080 §7): the hotel's trip, to keep or change.
  useIt: 'Use it',
  keepListed: (hotel: string) => `Keep it as it is, and it goes straight onto ${hotel}'s list when you finish.`,
};

/** "Add trip — € 58.50 a guest": the button shows the whole figure it saves. */
export function saveTripLabel(isNew: boolean, money: string | undefined, per: 'guest' | 'job' | undefined): string {
  const verb = isNew ? 'Add trip' : 'Save trip';
  return money && per ? `${verb} — ${money} ${perWords(per)}` : verb;
}

/** "3 guests = " · "€ 175.50" · "." — three parts, so only the figure is set in mono. */
export function readBack(
  minor: number,
  per: 'guest' | 'job',
  kind: 'experience' | 'transfer',
  maxGuests: number | undefined,
  money: (minor: number) => string,
): { lead: string; figure: string; tail: string } {
  const who = kind === 'transfer' ? 'passengers' : 'guests';
  if (per === 'job') {
    return { lead: '', figure: money(minor), tail: ` for the whole trip, however many come${maxGuests ? ` (up to ${maxGuests})` : ''}.` };
  }
  const n = maxGuests && maxGuests < 3 ? maxGuests : 3;
  return n === 1
    ? { lead: `1 ${who.slice(0, -1)} = `, figure: money(minor), tail: '.' }
    : { lead: `${n} ${who} = `, figure: money(minor * n), tail: '.' };
}

/** "Beach Resort pays you € 58.00 a guest for the Island Tour, 6 hours, up to 12 — keep it, or change it?" */
export function tripOffer(
  hotel: string,
  trip: { name: string; per: 'guest' | 'job'; durationHours?: number; maxGuests?: number },
  money: string,
): string {
  const hours = trip.durationHours ? `${trip.durationHours} hour${trip.durationHours === 1 ? '' : 's'}` : '';
  const facts = [hours, trip.maxGuests ? `up to ${trip.maxGuests}` : ''].filter(Boolean).map((f) => `, ${f}`).join('');
  return `${hotel} pays you ${money} ${perWords(trip.per)} for the ${trip.name}${facts} — keep it, or change it?`;
}

export const WORKING_HOURS = {
  usualHours: 'Usual hours',
  anyTime: 'Any time',
  theseHoursFrom: 'These hours: from',
  to: 'to',
  overnight: (from: string, to: string) => `${from} to ${to} the next morning.`,
  sameTime: 'The end has to differ from the start — or choose Any time.',
  bothTimes: 'Give a start and an end — or choose Any time.',
  seasonPair: 'Give both season months, or neither.',
  // What hours do today — true while nothing reads them (Principles Q5).
  whatHoursDo: 'Your hours are a note of when you usually work. Today no request is refused because of them.',
};

// The Business profile's place field, for a supplier who does transfers only
// (the phone's WHO_I_AM words).
export const PROFILE_PLACE = {
  place: 'Where you leave from',
  placeHint: 'e.g. Old Port, Chania',
  area: 'Which area do you cover?',
  areaHint: 'e.g. Chania and the airport',
};

// ── The hotel's Invite supplier form: the trip they already sell (N39) ─────
// DECISION-080 §7: the invitation can carry the trip and the hotel's margin, so
// the supplier's setup opens on it and Finish makes them bookable. The price is
// what the hotel pays the supplier today — never the guest price (DECISION-016).

export const INVITE_TRIP = {
  pageSub: 'A few fields, and the trip you already book with them if you like — they sign in on their phone and set up the rest.',
  title: 'The trip you already book with them (optional)',
  lede: "Their setup opens on this trip, to keep or change. Give your margin too: if they keep the trip as it is, they're bookable here the moment they finish; if they change it, you're asked to list it.",
  name: 'Trip name',
  namePlaceholder: 'Island Tour',
  kind: 'Kind',
  experience: 'Experience',
  transfer: 'Transfer',
  price: 'What you pay them for it today, before your margin',
  pricePlaceholder: '58,50',
  per: 'Per',
  perGuest: 'Per person',
  perJob: 'Whole boat or car',
  duration: 'Duration (hours)',
  durationPlaceholder: '6',
  guests: 'Max guests',
  guestsPlaceholder: '12',
  margin: 'Your margin on it',
  marginPlaceholder: '12',
  marginHint: 'Added on top of what you pay them — per person or for the whole trip, the same way as the price. Only your hotel sees it, never the supplier.',
  incomplete:
    "To send the trip, give its name, whether it's an experience or a transfer, what you pay them, and whether that's per person or for the whole boat or car — or leave the trip empty.",
  marginNeedsTrip: 'Add the trip this margin is for, or leave the margin empty.',
  priceBad: 'Type a figure — for example 58 or 58,50.',
  which: 'Which did you mean?',
  // N39 review: a number already on ISO gets a connection request; the trip is
  // not carried over (N42 builds that), and the hotel lists it itself.
  notCarried: (business: string) =>
    `The trip you entered wasn't sent: ${business} is already on ISO, so there is no invitation to carry it. Once they accept, add their trip in Products and set your margin there.`,
  notCarriedConnected: (business: string) =>
    `The trip you entered wasn't sent: ${business} is already connected with you. Add their trip in Products and set your margin there.`,
  // N42 fix round (correctness SHOULD 5): a request already waiting keeps the trip it was made with.
  notCarriedAsked: (business: string) =>
    `Your earlier request is waiting for ${business} in ISO; the trip you typed now wasn't added to it. Once they accept, you can list their trip in Products.`,
  notCarriedSettingUp: (business: string) =>
    `The trip you entered wasn't sent: ${business} is setting up from your earlier invitation, which carries its own trip.`,
};
